import type {
  PlannedRoutine,
  PlannedRoutineDTO,
  PlannedSession,
  PlannedSessionDTO,
  PlannedTreatment,
  PlannedTreatmentDTO,
  PlannedTreatmentResponse,
  PlannedTreatmentResponseDTO,
} from "@akina/domain";
import { type Mapper } from "./";

export const plannedTreatmentMapper: Mapper<PlannedTreatment, PlannedTreatmentDTO> = {
  toDomain: plannedTreatmentDTO => {
    const filteredIndices = plannedTreatmentDTO.session_key_sequence
      .map((key, idx) => (key.endsWith("00") ? -1 : idx))
      .filter(idx => idx !== -1);

    const sessionKeySequence = filteredIndices.map(idx => plannedTreatmentDTO.session_key_sequence[idx]);
    const sessionPlans = filteredIndices.map(idx => plannedSessionMapper.toDomain(plannedTreatmentDTO.session_plans[idx]));
    const filteredPhaseKeyAllocation = filteredIndices
      .map(idx => plannedTreatmentDTO.phase_key_allocation_to_session[idx])
      .filter(phaseKey => phaseKey !== "phase_0");

    return {
      id: plannedTreatmentDTO.id,
      treatmentId: plannedTreatmentDTO.treatment_id,
      treatmentTemplateKey: plannedTreatmentDTO.treatment_template_key,
      sessionKeySequence,
      sessionPlans,
      lastUpdate: plannedTreatmentDTO.last_update,
      phases: plannedTreatmentDTO.phases.phase_plans.map(phase => ({
        startWeek: phase.start_week,
        endWeek: phase.end_week,
        key: phase.key,
      })),
      phaseKeyAllocationToSession: filteredPhaseKeyAllocation,
      nextSessionPlanKey: plannedTreatmentDTO.next_session_plan_key,
      phaseLogs: plannedTreatmentDTO.phases.phase_logs
        ? Object.fromEntries(
            Object.entries(plannedTreatmentDTO.phases.phase_logs).map(([key, log]) => [
              key,
              {
                phaseKey: log.phase_key,
                startDate: log.start_date ? new Date(log.start_date) : null,
                endDate: log.end_date ? new Date(log.end_date) : null,
              },
            ]),
          )
        : undefined,
    };
  },
  toDTO: plannedTreatment => ({
    id: plannedTreatment.id,
    treatment_id: plannedTreatment.treatmentId,
    treatment_template_key: plannedTreatment.treatmentTemplateKey,
    session_key_sequence: plannedTreatment.sessionKeySequence,
    session_plans: plannedTreatment.sessionPlans.map(plannedSessionMapper.toDTO),
    last_update: plannedTreatment.lastUpdate,
    phases: {
      current_phase_key: "",
      phase_plans: plannedTreatment.phases.map(phase => ({
        start_week: phase.startWeek,
        end_week: phase.endWeek,
        key: phase.key,
      })),
      phase_logs: {},
    },
    phase_key_allocation_to_session: plannedTreatment.phaseKeyAllocationToSession,
    next_session_plan_key: plannedTreatment.nextSessionPlanKey,
  }),
};

export const plannedSessionMapper: Mapper<PlannedSession, PlannedSessionDTO> = {
  toDomain: plannedSessionDTO => ({
    id: plannedSessionDTO.id,
    key: plannedSessionDTO.key,
    treatmentId: plannedSessionDTO.treatment_id,
    treatmentTemplateKey: plannedSessionDTO.treatment_template_key,
    isAssessment: plannedSessionDTO.is_assessment,
    isInitialSession: plannedSessionDTO.is_initial_session,
    defaultInstancePerWeek: plannedSessionDTO.default_instance_per_week,
    maxInstancePerWeek: plannedSessionDTO.max_instance_per_week,
    earliestPossibleWeek: plannedSessionDTO.earliest_possible_week,
    latestPossibleWeek: plannedSessionDTO.latest_possible_week,
    sessionTemplateKey: plannedSessionDTO.session_template_key,
    routinePlanKeySequence: plannedSessionDTO.routine_plan_key_sequence,
    routinePlans: plannedSessionDTO.routine_plans.map(plannedRoutineMapper.toDomain),
  }),
  toDTO: plannedSession => ({
    id: plannedSession.id,
    key: plannedSession.key,
    treatment_id: plannedSession.treatmentId,
    treatment_template_key: plannedSession.treatmentTemplateKey,
    is_assessment: plannedSession.isAssessment,
    is_initial_session: plannedSession.isInitialSession,
    default_instance_per_week: plannedSession.defaultInstancePerWeek,
    max_instance_per_week: plannedSession.maxInstancePerWeek,
    earliest_possible_week: plannedSession.earliestPossibleWeek,
    latest_possible_week: plannedSession.latestPossibleWeek,
    session_template_key: plannedSession.sessionTemplateKey,
    routine_plan_key_sequence: plannedSession.routinePlanKeySequence,
    routine_plans: plannedSession.routinePlans.map(plannedRoutine => plannedRoutineMapper.toDTO(plannedRoutine)),
  }),
};

export const plannedRoutineMapper: Mapper<PlannedRoutine, PlannedRoutineDTO> = {
  toDomain: plannedRoutineDTO => ({
    id: plannedRoutineDTO.id,
    treatmentId: plannedRoutineDTO.treatment_id,
    treatmentTemplateKey: plannedRoutineDTO.treatment_template_key,
    key: plannedRoutineDTO.key,
    hasLaterality: plannedRoutineDTO.has_laterality,
    laterality: plannedRoutineDTO?.laterality,
    nSet: plannedRoutineDTO.n_set,
    nRepetitions: plannedRoutineDTO.n_repetitions,
    duration: plannedRoutineDTO.duration,
    breakDuration: plannedRoutineDTO.break_duration,
    order: plannedRoutineDTO.order,
    routineTemplateKey: plannedRoutineDTO.routine_template_key,
    exerciseTemplateKey: plannedRoutineDTO?.exercise_template_key,
    difficultyLevel: plannedRoutineDTO.difficulty_level,
  }),
  toDTO: plannedRoutine => ({
    id: plannedRoutine.id,
    treatment_id: plannedRoutine.treatmentId,
    treatment_template_key: plannedRoutine.treatmentTemplateKey,
    key: plannedRoutine.key,
    has_laterality: plannedRoutine.hasLaterality,
    laterality: plannedRoutine?.laterality,
    n_set: plannedRoutine.nSet,
    n_repetitions: plannedRoutine.nRepetitions,
    duration: plannedRoutine.duration,
    break_duration: plannedRoutine.breakDuration,
    order: plannedRoutine.order,
    routine_template_key: plannedRoutine.routineTemplateKey,
    exercise_template_key: plannedRoutine?.exerciseTemplateKey,
    difficulty_level: plannedRoutine.difficultyLevel,
  }),
};

export const plannedTreatmentResponseMapper: Mapper<PlannedTreatmentResponse, PlannedTreatmentResponseDTO> = {
  toDomain: plannedTreatmentResponseDTO => {
    const filteredIndices = plannedTreatmentResponseDTO.session_key_sequence
      .map((key, idx) => (key.endsWith("00") ? -1 : idx))
      .filter(idx => idx !== -1);

    const sessionKeySequence = filteredIndices.map(idx => plannedTreatmentResponseDTO.session_key_sequence[idx]);
    const sessionPlans = filteredIndices.map(idx => plannedSessionMapper.toDomain(plannedTreatmentResponseDTO.session_plans[idx]));
    const filteredPhaseKeyAllocation = filteredIndices
      .map(idx => plannedTreatmentResponseDTO.phase_key_allocation_to_session[idx])
      .filter(phaseKey => phaseKey !== "phase_0");

    return {
      treatmentId: plannedTreatmentResponseDTO.treatment_id,
      treatmentTemplateKey: plannedTreatmentResponseDTO.treatment_template_key,
      sessionKeySequence,
      sessionPlans,
      currentPhaseWeek: plannedTreatmentResponseDTO.current_phase_week,
      currentPhaseKey: plannedTreatmentResponseDTO.phases.current_phase_key,
      weekInTreatmentSinceStart: plannedTreatmentResponseDTO.week_in_treatment_since_start,
      lastUpdate: plannedTreatmentResponseDTO.last_update,
      phases: plannedTreatmentResponseDTO.phases.phase_plans.map(p => ({
        startWeek: p.start_week,
        endWeek: p.end_week,
        key: p.key,
      })),
      phaseKeyAllocationToSession: filteredPhaseKeyAllocation,
      nextSessionPlanKey: plannedTreatmentResponseDTO.next_session_plan_key,
      phaseLogs: plannedTreatmentResponseDTO.phases.phase_logs
        ? Object.fromEntries(
            Object.entries(plannedTreatmentResponseDTO.phases.phase_logs).map(([key, log]) => [
              key,
              {
                phaseKey: log.phase_key,
                startDate: log.start_date ? new Date(log.start_date) : null,
                endDate: log.end_date ? new Date(log.end_date) : null,
              },
            ]),
          )
        : undefined,
    };
  },
  toDTO: plannedTreatmentResponse => ({
    treatment_id: plannedTreatmentResponse.treatmentId,
    treatment_template_key: plannedTreatmentResponse.treatmentTemplateKey,
    session_key_sequence: plannedTreatmentResponse.sessionKeySequence,
    session_plans: plannedTreatmentResponse.sessionPlans.map(plannedSessionMapper.toDTO),
    current_phase_week: plannedTreatmentResponse.currentPhaseWeek,
    week_in_treatment_since_start: plannedTreatmentResponse.weekInTreatmentSinceStart,
    last_update: plannedTreatmentResponse.lastUpdate,
    phases: {
      current_phase_key: plannedTreatmentResponse.currentPhaseKey,
      phase_plans: plannedTreatmentResponse.phases.map(p => ({
        start_week: p.startWeek,
        end_week: p.endWeek,
        key: p.key,
      })),
      phase_logs: {},
    },
    phase_key_allocation_to_session: plannedTreatmentResponse.phaseKeyAllocationToSession,
    next_session_plan_key: plannedTreatmentResponse.nextSessionPlanKey,
  }),
};
